import React, { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { apiFetch } from "../api";

// Forgot-password entry point. Serves both roles:
//   /forgot-password               — bureau member (default)
//   /forgot-password?type=seeker   — profile seeker (public user)
// The two only differ in which endpoint we hit and which login page we
// fall back to. UI copy is identical otherwise.
function ForgotPassword() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const isSeeker = params.get("type") === "seeker";
  const endpoint = isSeeker
    ? "/public/auth/forgot-password"
    : "/auth/forgot-password";
  const loginPath = isSeeker ? "/login" : "/bureau";

  const [email, setEmail] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!email.trim()) {
      setError("Please enter your email");
      return;
    }
    setLoading(true);
    try {
      await apiFetch(endpoint, {
        method: "POST",
        body: JSON.stringify({ email: email.trim() }),
      });
      setSubmitted(true);
    } catch (err) {
      setError(err.message || "Failed to send reset link");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[var(--color-background)] px-4">
      <div className="w-full max-w-md rounded-2xl border border-[var(--color-border)] bg-[var(--color-surface)] p-6 shadow-md">
        <h1 className="text-xl font-bold text-[var(--color-text-strong)] mb-1">
          Forgot password
        </h1>
        <p className="text-sm text-[var(--color-text-muted)] mb-4">
          Enter the email you sign in with. If it's registered, we'll send
          you a reset link.
        </p>

        {submitted ? (
          <div className="space-y-3">
            <div className="rounded-lg border border-[var(--color-border)] bg-[var(--color-surface-muted)] px-3 py-3 text-sm text-[var(--color-text)]">
              If an account exists for that email, a reset link has been
              sent. Check your inbox (and spam folder) within the next few
              minutes. The link is valid for 30 minutes.
            </div>
            <button
              type="button"
              onClick={() => navigate(loginPath)}
              className="w-full rounded-lg border border-[var(--color-border)] py-2 text-sm font-semibold text-[var(--color-text)] hover:bg-[var(--color-surface-hover)]"
            >
              Back to login
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-[var(--color-text-muted)] mb-1">
                Email
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                autoCapitalize="off"
                autoCorrect="off"
                spellCheck="false"
                className="w-full rounded-lg border border-[var(--color-border)] px-3 py-2 text-sm"
                autoFocus
              />
            </div>
            {error && (
              <div className="rounded-lg border border-[var(--color-danger-border)] bg-[var(--color-danger-bg)] px-3 py-2 text-sm text-[var(--color-danger-text)]">
                {error}
              </div>
            )}
            <button
              type="submit"
              disabled={loading}
              className="w-full rounded-lg bg-[var(--color-primary)] py-2 text-sm font-semibold text-[var(--color-on-primary)] hover:bg-[var(--color-primary-hover)] disabled:opacity-60"
            >
              {loading ? "Sending…" : "Send reset link"}
            </button>
            <button
              type="button"
              onClick={() => navigate(loginPath)}
              className="w-full text-center text-sm text-[var(--color-text-muted)] hover:text-[var(--color-text-strong)]"
            >
              Back to login
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

export default ForgotPassword;
